<script lang="ts">
	import { page } from '$app/state'
	import { formInputColors } from '../styles'
	import PaginatorLink from './PaginatorLink.svelte'
	import PaginatorManualInput from './PaginatorManualInput.svelte'
	import { getLimitQueryParamKey } from './limit'
	import { getOffsetQueryParamKey } from './offset'
	import { _ } from '@repo/i18n'
	import { Icon } from '@repo/ui'
	import {
		ChevronDoubleLeft,
		ChevronDoubleRight,
		ChevronLeft,
		ChevronRight,
	} from '@steeze-ui/heroicons'

	interface Props {
		namespace?: string | null
		total?: number
		hasMore?: boolean
		lazy?: boolean
		visibleRowsCount?: number
		limitOptions?: any
		limit: number
		offset: number
	}

	let {
		namespace = null,
		total = 0,
		hasMore = false,
		lazy = false,
		visibleRowsCount = 0,
		limitOptions = [25, 50, 100, 500],
		limit = $bindable(),
		offset = $bindable(),
	}: Props = $props()

	let pages = $derived(Math.floor((total - 1) / limit))
	let currentPage = $derived(Math.floor(offset / limit) + (offset % limit ? 1 : 0))
	let nextPage = $derived(offset + limit)
	let previousPage = $derived(Math.max(offset - limit, 0))

	let fromItem = $derived(offset + 1)
	let toItem = $derived(Math.min(fromItem + limit - 1, total))

	function handlePageChange(event: CustomEvent) {
		updateSearchParams(limit, (event.detail - 1) * limit)
	}

	function handleLimitChange(event: Event) {
		updateSearchParams(Number((event.target as HTMLSelectElement).value), offset)
	}

	function updateSearchParams(newLimit: number, newOffset: number) {
		limit = newLimit
		offset = newOffset
	}

	function url(limit: number, offset: number) {
		const paginationLink = new URL(page.url)
		paginationLink.searchParams.set(getLimitQueryParamKey(namespace), limit.toString())
		paginationLink.searchParams.set(getOffsetQueryParamKey(namespace), offset.toString())
		return paginationLink.toString()
	}
</script>

<div class="flex justify-between gap-5 px-7 py-5 text-base max-sm:flex-col">
	<ul class="flex gap-2 max-sm:m-auto">
		<li>
			<PaginatorLink
				name="pagination-start"
				disabled={currentPage === 0}
				href={url(limit, 0)}
				onclick={(event) => {
					event.preventDefault()
					updateSearchParams(limit, 0)
				}}
			>
				<Icon src={ChevronDoubleLeft} />
			</PaginatorLink>
		</li>

		<li>
			<PaginatorLink
				name="pagination-previous"
				disabled={currentPage === 0}
				href={url(limit, previousPage)}
				onclick={(event) => {
					event.preventDefault()
					updateSearchParams(limit, previousPage)
				}}
			>
				<Icon src={ChevronLeft} />
			</PaginatorLink>
		</li>

		<li>
			<PaginatorManualInput {currentPage} {pages} on:submit={handlePageChange} />
		</li>

		{#if hasMore || currentPage < pages}
			<li>
				<PaginatorLink
					name="pagination-next"
					href={url(limit, nextPage)}
					onclick={(event) => {
						event.preventDefault()
						updateSearchParams(limit, nextPage)
					}}
				>
					<Icon src={ChevronRight} />
				</PaginatorLink>
			</li>
		{/if}

		{#if !lazy && total !== 0 && currentPage < pages}
			<li>
				<PaginatorLink
					name="pagination-end"
					href={url(limit, pages * limit)}
					onclick={(event) => {
						event.preventDefault()
						updateSearchParams(limit, pages * limit)
					}}
				>
					<Icon src={ChevronDoubleRight} />
				</PaginatorLink>
			</li>
		{/if}
	</ul>

	<div class="flex gap-5 max-sm:flex-col">
		<div class="flex items-center text-gray-400 max-sm:order-1 max-sm:m-auto">
			{#if lazy && visibleRowsCount <= limit}
				{$_('pagination.showing', {
					values: {
						from: fromItem,
						to: fromItem + visibleRowsCount - 1,
					},
				})}
				{$_('pagination.of')}
				{visibleRowsCount === limit && hasMore
					? $_('pagination.many')
					: fromItem + visibleRowsCount - 1}
			{:else}
				{$_('pagination.showing', { values: { from: fromItem, to: toItem } })}
				{$_('pagination.of')}
				{total}
			{/if}
		</div>
		<div class="flex items-center max-sm:m-auto">
			{$_('pagination.listing')}
			<div class="ml-2"></div>
			<select
				name="pagination-select"
				class="rounded-sm border {formInputColors}"
				onchange={handleLimitChange}
				value={limit}
			>
				{#each limitOptions as option (option)}
					<option value={option}>{option}</option>
				{/each}
			</select>
		</div>
	</div>
</div>
